<template>
  <div class="instagram-accounts-form">
    <box
      type="primary"
      :title="t('Franquias associadas')"
      elem-id="boxFranquiasInstagramAccount"
      :validation="() => generalValidationErrors.length > 0">

      <!-- Read-only account info -->
      <div v-if="isEditMode" class="account-info mb-4">
        <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <i class="pi pi-instagram text-2xl text-purple-600" />
          <div>
            <p class="font-bold">@{{ formData.ig_username }}</p>
            <p class="text-sm text-gray-500">{{ formData.page_name }}</p>
          </div>
        </div>
      </div>

      <div class="form-group">
        <FranchiseSelectMultiple
          :label="t('Franquias')"
          :placeholder="t('Selecione as franquias')"
          elem-id="instagram_accounts_franquia_ids"
          data-testid="instagram-accounts-franquias-select"
          :required="true"
          v-model="formData.franquia_ids">
        </FranchiseSelectMultiple>
      </div>
    </box>

    <box
      type="secondary"
      :title="t('Token de acesso')"
      elem-id="boxTokenInstagramAccount">
      <p class="text-sm text-gray-500 mb-3">
        {{ t('Cole aqui um token gerado manualmente no Meta Developer Dashboard para substituir o token atual.') }}
      </p>
      <div class="form-group">
        <input-text
          :label="t('Page Access Token')"
          :placeholder="t('Cole o token aqui (opcional)')"
          :required="false"
          elem-id="page_access_token"
          data-testid="instagram-accounts-token-input"
          v-model="formData.page_access_token">
        </input-text>
      </div>
    </box>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useInstagramAccounts } from '../../composables/useInstagramAccounts';
import { log } from '../../utils/DebugLogger';
import FranchiseSelectMultiple from '../../inputs/FranchiseSelectMultiple.vue';

type Id = number | string;

interface InstagramAccountData {
  instagram_account_id?: number;
  ig_username?: string;
  page_name?: string;
  franquia_ids?: Id[];
}

interface Props {
  isEditMode?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isEditMode: false,
});

const emit = defineEmits<{
  'save-success': [payload: { instagram_account_id: number }];
}>();

const { t } = useI18n();
const { fetchInstagramAccount, salvarFranquias, salvarToken } = useInstagramAccounts();

const isLoading = ref<boolean>(false);

const formData = reactive<{
  instagram_account_id: number;
  ig_username: string;
  page_name: string;
  franquia_ids: Id[];
  page_access_token: string;
}>({
  instagram_account_id: 0,
  ig_username: '',
  page_name: '',
  franquia_ids: [],
  page_access_token: '',
});

const isFranquiasValid = computed<boolean>(() => formData.franquia_ids && formData.franquia_ids.length > 0);

const generalValidationErrors = computed<string[]>(() => {
  const errors: string[] = [];
  if (!isFranquiasValid.value) errors.push(t('Selecione pelo menos uma franquia'));
  return errors;
});

const canSave = computed<boolean>(() => generalValidationErrors.value.length === 0);

const initializeForNew = async (): Promise<void> => {
  formData.instagram_account_id = 0;
  formData.ig_username = '';
  formData.page_name = '';
  formData.franquia_ids = [];
};

const loadForEdit = async (instagramAccountId: number): Promise<void> => {
  isLoading.value = true;
  try {
    const data = await fetchInstagramAccount(instagramAccountId) as InstagramAccountData;
    formData.instagram_account_id = data.instagram_account_id || 0;
    formData.ig_username = data.ig_username || '';
    formData.page_name = data.page_name || '';
    formData.franquia_ids = data.franquia_ids || [];
    formData.page_access_token = '';
  } catch (err) {
    eventBus.emit('globalMessage.error', t('Erro ao carregar dados'));
  } finally {
    isLoading.value = false;
  }
};

const save = async (): Promise<void> => {
  if (!canSave.value) {
    eventBus.emit('globalMessage.error', t('Selecione pelo menos uma franquia'));
    return;
  }
  isLoading.value = true;
  try {
    await salvarFranquias(formData.instagram_account_id, formData.franquia_ids);
    if (formData.page_access_token.trim()) {
      await salvarToken(formData.instagram_account_id, formData.page_access_token.trim());
    }
    emit('save-success', { instagram_account_id: formData.instagram_account_id });
  } catch (err) {
    const message = err instanceof Error ? err.message : '';
    eventBus.emit('globalMessage.error', message || t('Erro ao salvar'));
  } finally {
    isLoading.value = false;
  }
};

const resetForm = (): void => {
  formData.instagram_account_id = 0;
  formData.ig_username = '';
  formData.page_name = '';
  formData.franquia_ids = [];
  formData.page_access_token = '';
};

onMounted(() => log('InstagramAccountsForm', 'mounted', 'Component mounted'));

defineExpose({ initializeForNew, loadForEdit, save, resetForm, canSave });
</script>

<style scoped>
.instagram-accounts-form { display: flex; flex-direction: column; gap: 20px; }
.form-group { margin-bottom: 20px; }
.form-group:last-child { margin-bottom: 0; }
</style>
